import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Waves, Trophy, Landmark, Church, Folder, Music, Tent, GraduationCap, Heart, Building2, Users, Bike,
  Pencil, Trash2, Plus, Check, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PROFILE_ICONS = {
  waves: Waves, trophy: Trophy, landmark: Landmark, church: Church, folder: Folder, music: Music,
  tent: Tent, graduation: GraduationCap, heart: Heart, building: Building2, users: Users, bike: Bike,
};
const BASES = [
  ["algemeen", "Algemeen (secretariaat → bestuur → info)"],
  ["sport", "Sport (penningmeester → ledenadministratie → …)"],
  ["watersport", "Watersport (penningmeester → voorzitter → …)"],
  ["vve", "VvE (beheerder → bestuur → …)"],
  ["kerk", "Kerk (scriba → kerkenraad → …)"],
];

const IconPicker = ({ value, onChange, testid }) => (
  <div data-testid={testid} className="flex flex-wrap gap-1">
    {Object.entries(PROFILE_ICONS).map(([k, Icon]) => (
      <button key={k} type="button" onClick={() => onChange(k)} title={k} aria-label={`Icoon ${k}`} aria-pressed={value === k}
        className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors ${value === k ? "bg-[#FF4500] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
        <Icon className="w-4 h-4" strokeWidth={1.5} />
      </button>
    ))}
  </div>
);

const Row = ({ p, onChanged, onDeleted }) => {
  const [edit, setEdit] = useState(false);
  const [label, setLabel] = useState(p.label);
  const [icon, setIcon] = useState(p.iconName || "folder");
  const Icon = p.icon;
  const save = async () => {
    if (!label.trim()) return toast.error("Naam is verplicht");
    try {
      await axios.put(`${API}/profiles/${p.id}`, { label, icon });
      toast.success("Profiel bijgewerkt"); setEdit(false); onChanged();
    } catch { toast.error("Opslaan mislukt"); }
  };
  const remove = async () => {
    if (!window.confirm(`Profiel "${p.label}" verwijderen?`)) return;
    try {
      await axios.delete(`${API}/profiles/${p.id}`);
      toast.success("Profiel verwijderd"); onDeleted(p.id); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Verwijderen mislukt"); }
  };
  if (edit) {
    return (
      <li data-testid={`profile-row-${p.id}`} className="py-3 space-y-2">
        <div className="flex gap-2">
          <Input data-testid={`profile-label-input-${p.id}`} value={label} onChange={(e) => setLabel(e.target.value)} className="text-sm" />
          <Button data-testid={`profile-save-${p.id}`} size="sm" onClick={save} className="rounded-full bg-slate-900 text-white"><Check className="w-4 h-4" /></Button>
          <Button size="sm" variant="ghost" onClick={() => setEdit(false)} className="rounded-full"><X className="w-4 h-4" /></Button>
        </div>
        <IconPicker value={icon} onChange={setIcon} testid={`profile-icons-${p.id}`} />
      </li>
    );
  }
  return (
    <li data-testid={`profile-row-${p.id}`} className="py-2.5 flex items-center gap-3">
      <Icon className="w-4 h-4 text-slate-500" strokeWidth={1.5} />
      <div className="flex-1">
        <div className="text-sm font-medium">{p.label}</div>
        <div className="text-xs text-slate-400">{p.count ?? 0} {(p.count ?? 0) === 1 ? "lead" : "leads"} · basis: {p.base}</div>
      </div>
      <button data-testid={`profile-edit-${p.id}`} onClick={() => setEdit(true)} title="Hernoemen" className="text-slate-400 hover:text-slate-900"><Pencil className="w-4 h-4" /></button>
      <button data-testid={`profile-delete-${p.id}`} onClick={remove} disabled={(p.count ?? 0) > 0}
        title={(p.count ?? 0) > 0 ? "Alleen lege profielen kunnen worden verwijderd" : "Verwijderen"}
        className="text-slate-400 hover:text-red-600 disabled:opacity-30 disabled:hover:text-slate-400"><Trash2 className="w-4 h-4" /></button>
    </li>
  );
};

const NewProfile = ({ onChanged }) => {
  const [label, setLabel] = useState("");
  const [icon, setIcon] = useState("folder");
  const [base, setBase] = useState("algemeen");
  const create = async () => {
    if (!label.trim()) return toast.error("Geef het profiel een naam");
    try {
      const { data } = await axios.post(`${API}/profiles`, { label, short: label, icon, base });
      toast.success(`Profiel "${data.label}" aangemaakt — mailteksten overgenomen van ${base}`);
      setLabel(""); setIcon("folder"); setBase("algemeen"); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Aanmaken mislukt"); }
  };
  return (
    <div data-testid="profile-new" className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
      <div className="text-xs font-semibold text-slate-600">Nieuw profiel</div>
      <Input data-testid="profile-new-label" placeholder="Bijv. Muziekverenigingen" value={label} onChange={(e) => setLabel(e.target.value)} className="text-sm bg-white" />
      <label className="block text-xs text-slate-500">Lijkt op (bepaalt e-mailvoorkeur en start-mailteksten)
        <select data-testid="profile-new-base" value={base} onChange={(e) => setBase(e.target.value)}
          className="mt-1 w-full h-9 rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-800">
          {BASES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </label>
      <IconPicker value={icon} onChange={setIcon} testid="profile-new-icons" />
      <Button data-testid="profile-new-create" size="sm" onClick={create} className="rounded-full bg-[#FF4500] hover:bg-[#E63E00] text-white">
        <Plus className="w-4 h-4 mr-1" /> Profiel toevoegen
      </Button>
    </div>
  );
};

export const ProfilesManager = ({ open, onOpenChange, profiles, onChanged, onDeleted }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid="profiles-dialog" className="max-w-lg max-h-[90vh] overflow-auto">
      <DialogHeader>
        <DialogTitle className="font-display">Profielen beheren</DialogTitle>
        <DialogDescription>Hernoem profielen of voeg sectoren toe. Leads, CRM en mailteksten blijven per profiel bewaard.</DialogDescription>
      </DialogHeader>
      <ul className="divide-y divide-slate-100">
        {profiles.map((p) => <Row key={`${p.id}-${p.label}-${p.iconName}`} p={p} onChanged={onChanged} onDeleted={onDeleted} />)}
      </ul>
      <NewProfile onChanged={onChanged} />
    </DialogContent>
  </Dialog>
);
